import { whenLabel } from "../format";
import { distinctBranches, foldQuietChildren, middleEllipsis, sessionRows } from "../sessionTree";
import { toggleFamily, useExpandedFamilies } from "../hooks/useExpandedFamilies";
import type { SessionContact, TodoItem } from "../protocol";

/** "Mission control" grid shown when no session is selected — one card per session with its progress. */
export function Dashboard({
  orderedSessions,
  sessionTodos,
  previews,
  unreads,
  setSelected,
}: {
  orderedSessions: SessionContact[];
  sessionTodos: Record<string, TodoItem[]>;
  previews: Record<string, { text: string; ts: number }>;
  unreads?: Record<string, number>;
  setSelected: (key: string) => void;
}) {
  const expanded = useExpandedFamilies();
  const branches = distinctBranches(orderedSessions);
  const entries = foldQuietChildren(
    sessionRows(orderedSessions),
    expanded,
    (s) => (unreads?.[`${s.pairingId}/${s.sessionId}`] ?? 0) > 0,
  );
  return (
    <div className="dashboard">
      <div className="dashboard-head">Mission control · {orderedSessions.length} session{orderedSessions.length === 1 ? "" : "s"}</div>
      {orderedSessions.length === 0 ? (
        <p className="muted" style={{ padding: 20 }}>
          No active sessions yet.
        </p>
      ) : (
        <div className="dashboard-grid">
          {/* Ordered, not nested: the grid wraps, so a worktree cannot sit UNDER its checkout the way a list
              row does. Following it is what is available, and it keeps three cards of one repository from
              scattering across the grid with only their chips to relate them. */}
          {entries.map((e) => {
            if (e.kind === "fold") {
              return (
                <button
                  key={`fold:${e.parentKey}`}
                  className="dash-card dash-fold"
                  aria-expanded={e.expanded}
                  onClick={() => toggleFamily(e.parentKey)}
                >
                  {e.expanded ? "▾ Show fewer" : `▸ ${e.hidden} more worktree${e.hidden === 1 ? "" : "s"}`}
                </button>
              );
            }
            const { session: s, depth } = e.row;
            const key = `${s.pairingId}/${s.sessionId}`;
            const ts = sessionTodos[key] ?? [];
            const done = ts.filter((t) => t.status === "completed").length;
            const active = ts.find((t) => t.status === "in_progress");
            const pct = ts.length ? Math.round((done / ts.length) * 100) : 0;
            const pv = previews[key];
            return (
              <button
                key={key}
                className={`dash-card${depth ? " child" : ""}`}
                onClick={() => setSelected(key)}
              >
                <div className="dash-card-head">
                  <span
                    className={`dot${
                      s.online
                        ? s.agentAttached === false
                          ? " away"
                          : s.awaiting
                            ? " awaiting"
                            : s.idle
                              ? " idle"
                              : " on"
                        : s.asleep
                          ? " asleep"
                          : ""
                    }`}
                    title={s.asleep ? "Asleep: a message wakes it" : !s.online && s.host === "conductor" ? "In Conductor: messages wait until you open it there" : undefined}
                  />
                  <span className="dash-card-name">{s.label}</span>
                  {branches.has(key) ? (
                    <span className="row-branch" title={s.branch}>
                      {middleEllipsis(branches.get(key)!, 28)}
                    </span>
                  ) : null}
                  {pv?.ts ? <span className="dash-card-time">{whenLabel(pv.ts)}</span> : null}
                </div>
                {pv?.text ? <div className="dash-card-preview">{pv.text}</div> : null}
                {ts.length > 0 && (
                  <>
                    <div className="dash-progress">
                      <div className="dash-progress-bar" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="dash-todo-line">
                      {done}/{ts.length} done{active ? ` · ${active.content}` : ""}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
